/**
 * SegmentList — displays Couinaud segments and tumors with color pills and volumes.
 *
 * Supports checkbox selection mode for building perfused volumes.
 * Hover highlighting updates display feature opacity via the display manager.
 */

import { SEGMENT_COLORS, TUMOR_COLOR, ROMAN_NUMERALS, rgbToCss } from '../domain/segment-colors';

import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface SegmentEntry {
    labelValue: number;
    name: string;
    volumeMl: number;
}

export interface SegmentListProps {
    segments: readonly SegmentEntry[];
    tumors: readonly SegmentEntry[];
    /** Total liver volume (sum of all segments) */
    totalLiverVolumeMl: number;
    /** If provided, enables checkbox selection mode */
    selectedSegments?: ReadonlySet<number>;
    selectedTumors?: ReadonlySet<number>;
    onToggleSegment?: (labelValue: number) => void;
    onToggleTumor?: (labelValue: number) => void;
    onHoverSegment?: (labelValue: number | null) => void;
    onHoverTumor?: (labelValue: number | null) => void;
}

// ============================================================================
// Helpers
// ============================================================================

function ColorPill({ color }: { color: readonly [number, number, number] }) {
    return (
        <span
            className="inline-block h-3 w-3 shrink-0 rounded-sm"
            style={{ backgroundColor: rgbToCss(color) }}
        />
    );
}

// ============================================================================
// Component
// ============================================================================

export function SegmentList({
    segments,
    tumors,
    totalLiverVolumeMl,
    selectedSegments,
    selectedTumors,
    onToggleSegment,
    onToggleTumor,
    onHoverSegment,
    onHoverTumor,
}: SegmentListProps) {
    const selectable = !!onToggleSegment;

    return (
        <div className="flex flex-col">
            {/* Segments section */}
            <div className="border-b border-border/50">
                <div className="flex w-full items-center gap-1 px-2 py-2 text-sm font-semibold font-mono uppercase tracking-wide text-muted-foreground">
                    [Segments]
                </div>
                <div className="pb-2">
                    {segments.map((seg) => {
                        const color = SEGMENT_COLORS[seg.labelValue] ?? [0.6, 0.6, 0.6];
                        const checked = selectedSegments?.has(seg.labelValue) ?? false;
                        const roman = ROMAN_NUMERALS[seg.labelValue] ?? String(seg.labelValue);

                        return (
                            <div
                                key={`seg-${seg.labelValue}`}
                                className={cn(
                                    'group flex cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-sm transition-colors',
                                    'text-muted-foreground hover:bg-muted hover:text-foreground'
                                )}
                                onMouseEnter={() => onHoverSegment?.(seg.labelValue)}
                                onMouseLeave={() => onHoverSegment?.(null)}
                                onClick={() => onToggleSegment?.(seg.labelValue)}
                            >
                                {selectable && (
                                    <Checkbox
                                        checked={checked}
                                        className="h-3.5 w-3.5"
                                        onClick={(e) => e.stopPropagation()}
                                        onCheckedChange={() => onToggleSegment?.(seg.labelValue)}
                                    />
                                )}
                                <ColorPill color={color} />
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="min-w-0 flex-1 truncate font-mono font-medium">
                                            {roman}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{seg.name}</TooltipContent>
                                </Tooltip>
                                <span className="w-20 shrink-0 text-right tabular-nums font-mono text-xs">
                                    {seg.volumeMl.toFixed(1)} mL
                                </span>
                            </div>
                        );
                    })}
                    {/* Total */}
                    <div className="flex items-center gap-1.5 px-2 py-1 text-sm font-semibold text-muted-foreground">
                        <span className="flex-1 font-mono text-xs">Total</span>
                        <span className="w-20 shrink-0 text-right tabular-nums font-mono text-xs">
                            {totalLiverVolumeMl.toFixed(1)} mL
                        </span>
                    </div>
                </div>
            </div>

            {/* Tumors section */}
            {tumors.length > 0 && (
                <div className="border-b border-border/50 last:border-b-0">
                    <div className="flex w-full items-center gap-1 px-2 py-2 text-sm font-semibold font-mono uppercase tracking-wide text-muted-foreground">
                        [Tumors]
                    </div>
                    <div className="pb-2">
                        {tumors.map((tumor) => {
                            const checked = selectedTumors?.has(tumor.labelValue) ?? false;

                            return (
                                <div
                                    key={`tumor-${tumor.labelValue}`}
                                    className={cn(
                                        'group flex cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-sm transition-colors',
                                        'text-muted-foreground hover:bg-muted hover:text-foreground'
                                    )}
                                    onMouseEnter={() => onHoverTumor?.(tumor.labelValue)}
                                    onMouseLeave={() => onHoverTumor?.(null)}
                                    onClick={() => onToggleTumor?.(tumor.labelValue)}
                                >
                                    {selectable && (
                                        <Checkbox
                                            checked={checked}
                                            className="h-3.5 w-3.5"
                                            onClick={(e) => e.stopPropagation()}
                                            onCheckedChange={() => onToggleTumor?.(tumor.labelValue)}
                                        />
                                    )}
                                    <ColorPill color={TUMOR_COLOR} />
                                    <span className="min-w-0 flex-1 truncate font-mono font-medium">
                                        {tumor.name}
                                    </span>
                                    <span className="w-20 shrink-0 text-right tabular-nums font-mono text-xs">
                                        {tumor.volumeMl.toFixed(1)} mL
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
